/**
 * Session Security Utilities
 *
 * Provides idle timeout, session expiration, and device fingerprinting
 * for enhanced admin panel security.
 */

// Session configuration
export const SESSION_CONFIG = {
  // Maximum session age in milliseconds (7 days)
  MAX_SESSION_AGE_MS: 7 * 24 * 60 * 60 * 1000,
  // Idle timeout in milliseconds (30 minutes)
  IDLE_TIMEOUT_MS: 30 * 60 * 1000,
  // Warning before logout in milliseconds (2 minutes)
  IDLE_WARNING_MS: 2 * 60 * 1000,
  // Storage keys
  STORAGE_KEYS: {
    SESSION_START: 'kes_session_start',
    LAST_ACTIVITY: 'kes_last_activity',
    DEVICE_FINGERPRINT: 'kes_device_fingerprint',
  }
} as const;

/**
 * Generate a SHA-256 hash of a string using Web Crypto API
 */
const sha256 = async (message: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Generate a secure device fingerprint based on browser characteristics
 * Uses SHA-256 hashing for cryptographic security
 */
export const generateDeviceFingerprint = async (): Promise<string> => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width.toString(),
    screen.height.toString(),
    screen.colorDepth.toString(),
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency?.toString() || 'unknown',
    // Canvas fingerprint (simplified)
    getCanvasFingerprint(),
  ];

  // Create a SHA-256 hash from components
  const fingerprint = components.join('|');
  const hash = await sha256(fingerprint);
  return hash.substring(0, 32);
};

/**
 * Simple canvas fingerprint for additional uniqueness
 */
const getCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('KES-fingerprint', 2, 2);

    return canvas.toDataURL().slice(-50);
  } catch {
    return 'canvas-error';
  }
};

/**
 * Store session start time
 */
export const setSessionStart = (): void => {
  localStorage.setItem(
    SESSION_CONFIG.STORAGE_KEYS.SESSION_START,
    Date.now().toString()
  );
};

/**
 * Get session start time
 */
export const getSessionStart = (): number | null => {
  const stored = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.SESSION_START);
  return stored ? parseInt(stored, 10) : null;
};

/**
 * Check if session has exceeded max age
 */
export const isSessionExpired = (): boolean => {
  const sessionStart = getSessionStart();
  if (!sessionStart) return true;

  const elapsed = Date.now() - sessionStart;
  return elapsed > SESSION_CONFIG.MAX_SESSION_AGE_MS;
};

/**
 * Get remaining session time in milliseconds
 */
export const getRemainingSessionTime = (): number => {
  const sessionStart = getSessionStart();
  if (!sessionStart) return 0;

  const elapsed = Date.now() - sessionStart;
  const remaining = SESSION_CONFIG.MAX_SESSION_AGE_MS - elapsed;
  return Math.max(0, remaining);
};

/**
 * Update last activity timestamp
 */
export const updateLastActivity = (): void => {
  localStorage.setItem(
    SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
    Date.now().toString()
  );
};

/**
 * Get last activity timestamp
 */
export const getLastActivity = (): number | null => {
  const stored = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
  return stored ? parseInt(stored, 10) : null;
};

/**
 * Check if user has been idle too long
 */
export const isUserIdle = (): boolean => {
  const lastActivity = getLastActivity();
  if (!lastActivity) return false;

  const idleTime = Date.now() - lastActivity;
  return idleTime > SESSION_CONFIG.IDLE_TIMEOUT_MS;
};

/**
 * Get idle time in milliseconds
 */
export const getIdleTime = (): number => {
  const lastActivity = getLastActivity();
  if (!lastActivity) return 0;

  return Date.now() - lastActivity;
};

/**
 * Check if idle warning should be shown
 */
export const shouldShowIdleWarning = (): boolean => {
  const idleTime = getIdleTime();
  const warningThreshold = SESSION_CONFIG.IDLE_TIMEOUT_MS - SESSION_CONFIG.IDLE_WARNING_MS;
  return idleTime > warningThreshold && idleTime < SESSION_CONFIG.IDLE_TIMEOUT_MS;
};

/**
 * Get time until idle timeout in milliseconds
 */
export const getTimeUntilIdleTimeout = (): number => {
  const idleTime = getIdleTime();
  return Math.max(0, SESSION_CONFIG.IDLE_TIMEOUT_MS - idleTime);
};

/**
 * Store device fingerprint
 */
export const storeDeviceFingerprint = (fingerprint: string): void => {
  localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.DEVICE_FINGERPRINT, fingerprint);
};

/**
 * Get stored device fingerprint
 */
export const getStoredFingerprint = (): string | null => {
  return localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.DEVICE_FINGERPRINT);
};

/**
 * Validate current device against stored fingerprint
 * Returns true if fingerprint matches or no fingerprint stored
 */
export const validateDeviceFingerprint = async (): Promise<boolean> => {
  const storedFingerprint = getStoredFingerprint();
  if (!storedFingerprint) return true; // No fingerprint to compare

  const currentFingerprint = await generateDeviceFingerprint();
  return storedFingerprint === currentFingerprint;
};

/**
 * Clear all session security data
 */
export const clearSessionData = (): void => {
  localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.SESSION_START);
  localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
  localStorage.removeItem(SESSION_CONFIG.STORAGE_KEYS.DEVICE_FINGERPRINT);
};

/**
 * Initialize session security for a new login
 */
export const initializeSession = async (): Promise<void> => {
  const fingerprint = await generateDeviceFingerprint();
  setSessionStart();
  updateLastActivity();
  storeDeviceFingerprint(fingerprint);
};

/**
 * Format time remaining for display
 */
export const formatTimeRemaining = (ms: number): string => {
  if (ms <= 0) return '0:00';

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format session age for display
 */
export const formatSessionAge = (): string => {
  const sessionStart = getSessionStart();
  if (!sessionStart) return 'Unknown';

  const elapsed = Date.now() - sessionStart;
  const hours = Math.floor(elapsed / (60 * 60 * 1000));
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const minutes = Math.floor(elapsed / (60 * 1000));
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }
};
